import { useCallback, useRef, useState } from 'react';
import type { AiQuickTask } from '@awwyeah/shared';
import { AssistantNappingError, postAiQuick } from './api';

/**
 * `useAiQuick` — the one client-side entry point for the interactive AI tasks
 * (`parse-event`, `parse-list`, `edit-request`).
 *
 * It returns a **proposal**, never a write. Every caller is expected to show the proposal,
 * let a human confirm it, and then do the Yjs write itself (ROADMAP §1.3 — the server never
 * writes collaborative state for an interactive task).
 *
 * WS-18 ships the hook and its endpoint but deliberately no UI that calls it: the
 * event/list capture surfaces are a later workstream. Building the plumbing now means that
 * workstream is a component, not a component plus a protocol.
 */

export type AiQuickState<T> =
  | { phase: 'idle' }
  | { phase: 'thinking' }
  | { phase: 'ready'; proposal: T }
  /** The assistant is down or the device is offline. Not an error to apologise for. */
  | { phase: 'napping' }
  | { phase: 'failed'; error: string };

export interface UseAiQuick<T> {
  state: AiQuickState<T>;
  /** Runs the task. Resolves to the proposal, or `null` when it couldn't be produced. */
  ask: (text: string, context?: Record<string, unknown>) => Promise<T | null>;
  reset: () => void;
}

export function useAiQuick<T = unknown>(task: AiQuickTask): UseAiQuick<T> {
  const [state, setState] = useState<AiQuickState<T>>({ phase: 'idle' });
  /** Guards against a slow first call overwriting a newer one's result. */
  const requestId = useRef(0);

  const ask = useCallback(
    async (text: string, context?: Record<string, unknown>): Promise<T | null> => {
      const id = ++requestId.current;
      setState({ phase: 'thinking' });
      try {
        const { proposal } = await postAiQuick(task, text, context);
        if (id !== requestId.current) return null;
        setState({ phase: 'ready', proposal: proposal as T });
        return proposal as T;
      } catch (error) {
        if (id !== requestId.current) return null;
        setState(
          error instanceof AssistantNappingError
            ? { phase: 'napping' }
            : { phase: 'failed', error: (error as Error).message },
        );
        return null;
      }
    },
    [task],
  );

  const reset = useCallback(() => {
    requestId.current++;
    setState({ phase: 'idle' });
  }, []);

  return { state, ask, reset };
}
